import {
	BigNum,
	BN,
	VelocityClient,
	MarketType,
	maxSizeForTargetLiabilityWeightBN,
	PerpMarketAccount,
	PRICE_PRECISION_EXP,
	User,
} from '@velocity-exchange/sdk';
import {
	UIMarket,
	getBaseAssetSymbol,
	getMarginUsedForPosition as getMarginUsedForPosition_dep,
	convertLeverageToMarginRatio,
	getMaxLeverageForMarket,
	getUserMaxLeverageForMarket as getUserMaxLeverageForMarket_dep,
} from '@velocity-exchange/common';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import { formatText, PRESETS } from '@velocity-exchange/common/format';
import { roundBigNumToDecimalPlace } from '@velocity-exchange/common';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';

export const getSpotLiqPriceNum = (
	user: User,
	marketIndex: number,
	baseSize?: BigNum,
	isLong = true
): number => {
	if (!user || !user.isSubscribed) return 0;

	let baseAssetBN = baseSize ? baseSize.val : undefined;

	if (!isLong && baseAssetBN) {
		baseAssetBN = baseAssetBN.mul(new BN(-1));
	}

	const liqPriceBn = user.spotLiquidationPrice(marketIndex, baseAssetBN);

	const liqPriceNum = BigNum.from(liqPriceBn, PRICE_PRECISION_EXP).toNum();

	return liqPriceNum < 0 ? 0 : liqPriceNum;
};

export const getSpotLiqPriceStr = (
	user: User,
	marketIndex: number,
	baseSize?: BigNum,
	isLong = true
): string => {
	if (!user || !user.isSubscribed) return '';

	const liqPriceNum = getSpotLiqPriceNum(user, marketIndex, baseSize, isLong);

	if (liqPriceNum <= 0) return PRICE_FALLBACK_DISPLAY_VALUE;

	const liqPriceUi = formatText(liqPriceNum, PRESETS.displayPrice);

	return `$${liqPriceUi}`;
};

export const getAccountsInUse = (user: User): number => {
	if (!user || !user.isSubscribed) return 0;
	return (
		user.getActivePerpPositions().length + user.getActiveSpotPositions().length
	);
};

export const baseSizeDisplayForPerpMarket = (
	baseSize: BigNum,
	marketSymbol: string,
	hideValue: boolean,
	decimals?: number
) => {
	if (decimals) {
		baseSize = roundBigNumToDecimalPlace(baseSize, decimals);
	}

	return `${
		hideValue ? '∗∗∗∗' : baseSize.prettyPrint()
	} ${getBaseAssetSymbol(marketSymbol)}`;
};

export const getMarginUsedForPosition = getMarginUsedForPosition_dep;

/**
 * Returns the maximum position size for the given leverage, or `null` when the
 * market imposes no size limit at that leverage. A `null` result must NOT be
 * collapsed into ZERO — the SDK uses `null` to mean "unbounded" (e.g. when
 * imfFactor is unset, as on devnet), whereas a real ZERO means "max size ~0".
 */
export const calculateMaxPositionSize = (
	leverage: number,
	marketAccount: PerpMarketAccount | undefined
): BN | null => {
	if (!marketAccount) return null;

	const targetMarginRatio = convertLeverageToMarginRatio(leverage);

	return maxSizeForTargetLiabilityWeightBN(
		new BN(targetMarginRatio ?? 0),
		new BN(marketAccount.imfFactor),
		new BN(marketAccount.marginRatioInitial),
		marketAccount
	);
};

export const getUserMaxLeverageForMarket = (
	marketIndex: number,
	velocityClient: VelocityClient,
	userClient: User,
	uiMarket: UIMarket
) => {
	const { maxLeverage } = getMaxLeverageForMarket(
		MarketType.PERP,
		marketIndex,
		velocityClient
	);
	const savedMaxLeverage = useMarketSettingsStore
		.getState()
		.getMaxLeverageForMarket(uiMarket.market.symbol);
	const positionMaxLeverage = getUserMaxLeverageForMarket_dep(
		userClient,
		marketIndex,
		maxLeverage,
		savedMaxLeverage
	);

	return positionMaxLeverage;
};
